import { devWarn } from '@/libs/devLog'

import {
  pickRecommendBranch,
  type IBookingRecommend,
  type IBranchOption,
  type ICheckAvailabilityResponse,
  type IRedeemRecommend
} from '../types/schema'

/** `HH:MM:SS` or `HH:MM` -> `HH:MM`. The booking API sends seconds; the form and slot list do not. */
const toHHMM = (value: string) => value.slice(0, 5)

/** Long name, as the dropdown shows it — not the API's short `branch_name2`, which won't match. */
function nameFor(branches: IBranchOption[], branchId: number, fallback = ''): string {
  return branches.find((branch) => Number(branch.branch_id) === branchId)?.branch_long_name ?? fallback
}

const MESSAGE_LEAD = 'but you can book at'
const TIMES_ONLY = /^[\s\d:,.]+$/
const HHMM = /\b\d{1,2}:[0-5]\d\b/g

/**
 * Stopgap: the deployed API puts its suggestions only in `message`, never `recommendedTimes`.
 * Same-branch sentence only — the cross-branch one interleaves branch names with times and cannot
 * map back to ids, hence the letters check. Delete once the API sends the array.
 */
function timesFromMessage(message?: string): string[] {
  const tail = message?.split(MESSAGE_LEAD)[1]
  if (!tail || !TIMES_ONLY.test(tail)) return []

  return (tail.match(HHMM) ?? []).map((time) => time.padStart(5, '0'))
}

/**
 * Normalises the pre-flight answer. Same-branch and other-branch alternatives never both arrive:
 * `recommendedTimes` is empty when the answer is elsewhere, so a caller reading only that field
 * cannot silently rebook the same location.
 */
export function fromAvailability(
  response: ICheckAvailabilityResponse,
  branches: IBranchOption[],
  requestedBranchId: number
): IRedeemRecommend {
  const reason = response.reason ?? null
  const structured = response.recommendedTimes?.length ? response.recommendedTimes.map(toHHMM) : []
  const sameBranchTimes = structured.length ? structured : timesFromMessage(response.message)

  if (sameBranchTimes.length) {
    if (!structured.length) {
      devWarn('[redeem] recommendedTimes absent — times recovered from `message`', response)
    }

    return {
      reason,
      options: [
        {
          branchId: requestedBranchId,
          branchName: nameFor(branches, requestedBranchId, response.branch_name2 ?? ''),
          isCurrentBranch: true,
          times: sameBranchTimes
        }
      ]
    }
  }

  const alternatives = (response.alternativeBranches ?? [])
    .map((branch) => ({
      branchId: branch.branch_id,
      branchName: nameFor(branches, branch.branch_id, branch.branch_name2),
      isCurrentBranch: branch.branch_id === requestedBranchId,
      times: (branch.times ?? []).map(toHHMM)
    }))
    .filter((option) => option.times.length > 0)

  // Same blank panel as the booking path, so it needs the same trace.
  if (!alternatives.length) {
    devWarn('[redeem] availability check said no, with no times to offer', response)
  }

  return { reason, options: alternatives }
}

/**
 * Normalises the booking API's `create_status: false` reply — only reached when the pre-flight was
 * skipped or disagreed. One branch, or `[]` when it found none.
 */
export function fromBookingRecommend(
  recommend: IBookingRecommend,
  branches: IBranchOption[],
  requestedBranchId: number
): IRedeemRecommend {
  const branch = pickRecommendBranch(recommend?.branch)
  const times = Array.isArray(recommend?.time) ? recommend.time : []

  // Only place that can tell "API found nothing" from "we could not read it". Say which.
  if (!branch || !times.length) {
    devWarn('[redeem] no usable recommendation in the booking reply', recommend)
    return { reason: null, options: [] }
  }

  return {
    reason: null,
    options: [
      {
        branchId: branch.branch_id,
        branchName: nameFor(branches, branch.branch_id, branch.branch_long_name),
        isCurrentBranch: branch.branch_id === requestedBranchId,
        times: times.map(toHHMM)
      }
    ]
  }
}
